/**
 * Price Filter Component
 *
 * Dual-handle price range slider with a non-linear (power-curve) scale, plus two
 * editable min/max text inputs. Rendered by snippets/filter-price.liquid.
 *
 * @component price-filter
 * @extends HTMLElement
 *
 * @scale
 * Shopify's price data is dense at the low end (most products are cheap relative
 * to the single most expensive one). A linear slider would cram that popular band
 * into a sliver of the track. Instead the handle position maps to value as:
 *
 *   value = min + (max - min) * pos ^ exponent          (pos in [0, 1])
 *   pos   = ((value - min) / (max - min)) ^ (1 / exponent)
 *
 * With exponent > 1 the low prices occupy a larger share of the track, giving
 * finer control there — matching the design's finer low-price buckets. Tune via
 * the `data-exponent` attribute.
 *
 * @currency
 * Values from Shopify are in the currency subunit (cents/øre). Internally we keep
 * subunits; we divide by 100 for display and for the gte/lte query params, which
 * Shopify expects in major units.
 *
 * @integration
 * On commit (handle release or input Enter/blur) it updates its own
 * data-value-min / data-value-max attributes and dispatches `filter:change`.
 * collection-filter.js reads those attributes in buildFilterFormData() and
 * appends the gte/lte params. It also exposes reset() and restoreFromURL() for
 * the clear-all and cancel-dialog flows.
 */
class PriceFilter extends HTMLElement {
  connectedCallback() {
    this.min = Number(this.dataset.min) || 0;
    this.max = Number(this.dataset.max) || 0;
    this.exponent = Number(this.dataset.exponent) || 2;
    this.gteName = this.dataset.gteName;
    this.lteName = this.dataset.lteName;
    this.currency = this.dataset.currency || '';
    this.locale = this.dataset.locale || 'en';

    this.minVal = clamp(Number(this.dataset.valueMin) || this.min, this.min, this.max);
    this.maxVal = clamp(Number(this.dataset.valueMax) || this.max, this.min, this.max);

    this.track = this.querySelector('[data-track]');
    this.rangeEl = this.querySelector('[data-range]');
    this.minHandle = this.querySelector('[data-min-handle]');
    this.maxHandle = this.querySelector('[data-max-handle]');
    this.minInput = this.querySelector('[data-min-input]');
    this.maxInput = this.querySelector('[data-max-input]');

    // Nothing to slide over — leave the (server-rendered) inputs as-is.
    if (!this.track || this.max <= this.min) {
      this.render();
      return;
    }

    this.bindHandle(this.minHandle, 'min');
    this.bindHandle(this.maxHandle, 'max');
    this.bindInput(this.minInput, 'min');
    this.bindInput(this.maxInput, 'max');

    this.render();
  }

  // --- scale ---------------------------------------------------------------

  valueToPos(value) {
    if (this.max <= this.min) return 0;
    const ratio = clamp((value - this.min) / (this.max - this.min), 0, 1);
    return Math.pow(ratio, 1 / this.exponent);
  }

  posToValue(pos) {
    const p = clamp(pos, 0, 1);
    return this.min + (this.max - this.min) * Math.pow(p, this.exponent);
  }

  /** Snap a subunit value to a tidy major-unit step for nicer numbers. */
  snap(value) {
    const major = value / 100;
    let step;
    if (major < 500) step = 10;
    else if (major < 2000) step = 50;
    else step = 100;
    return Math.round(major / step) * step * 100;
  }

  // --- rendering -----------------------------------------------------------

  render() {
    const minPos = this.valueToPos(this.minVal) * 100;
    const maxPos = this.valueToPos(this.maxVal) * 100;

    if (this.minHandle) {
      this.minHandle.style.left = `${minPos}%`;
      this.describeHandle(this.minHandle, this.min, this.maxVal, this.minVal);
    }
    if (this.maxHandle) {
      this.maxHandle.style.left = `${maxPos}%`;
      this.describeHandle(this.maxHandle, this.minVal, this.max, this.maxVal);
    }
    if (this.rangeEl) {
      this.rangeEl.style.left = `${minPos}%`;
      this.rangeEl.style.width = `${Math.max(0, maxPos - minPos)}%`;
    }
    if (this.minInput) this.minInput.value = this.format(this.minVal);
    if (this.maxInput) this.maxInput.value = this.format(this.maxVal);

    // Keep the payload source of truth in sync for collection-filter.js.
    this.dataset.valueMin = String(Math.round(this.minVal));
    this.dataset.valueMax = String(Math.round(this.maxVal));
  }

  /**
   * Keep a handle's ARIA slider values in step with the visual position.
   *
   * Each thumb of a two-thumb slider is bounded by the other one, so the bounds
   * passed in are the *effective* range for that handle, not the track's extent.
   * Values are announced in major units; aria-valuetext adds the currency so
   * screen readers say "1,200 DKK" rather than a bare number.
   */
  describeHandle(handle, lower, upper, value) {
    handle.setAttribute('aria-valuemin', String(Math.round(lower / 100)));
    handle.setAttribute('aria-valuemax', String(Math.round(upper / 100)));
    handle.setAttribute('aria-valuenow', String(Math.round(value / 100)));
    handle.setAttribute('aria-valuetext', this.format(value));
  }

  format(subunits) {
    const major = Math.round(subunits / 100);
    const number = major.toLocaleString(this.locale);
    return this.currency ? `${number} ${this.currency}` : `${number}`;
  }

  parse(text) {
    const digits = String(text).replace(/[^\d]/g, '');
    return digits === '' ? null : Number(digits) * 100; // major -> subunits
  }

  // --- interaction ---------------------------------------------------------

  bindHandle(handle, which) {
    if (!handle) return;

    const onPointerDown = (e) => {
      e.preventDefault();
      handle.setPointerCapture?.(e.pointerId);

      const onMove = (moveEvent) => {
        const rect = this.track.getBoundingClientRect();
        const pos = (moveEvent.clientX - rect.left) / rect.width;
        this.setValue(which, this.snap(this.posToValue(pos)), false);
      };

      const onUp = (upEvent) => {
        handle.releasePointerCapture?.(upEvent.pointerId);
        handle.removeEventListener('pointermove', onMove);
        handle.removeEventListener('pointerup', onUp);
        handle.removeEventListener('pointercancel', onUp);
        this.commit();
      };

      handle.addEventListener('pointermove', onMove);
      handle.addEventListener('pointerup', onUp);
      handle.addEventListener('pointercancel', onUp);
    };

    handle.addEventListener('pointerdown', onPointerDown);

    handle.addEventListener('keydown', (e) => {
      const step = Math.max(100, Math.round((this.max - this.min) / 100)); // ~1% of range
      let delta = 0;
      if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') delta = -step;
      else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') delta = step;
      else return;

      e.preventDefault();
      const current = which === 'min' ? this.minVal : this.maxVal;
      this.setValue(which, this.snap(current + delta), false);
      this.commit();
    });
  }

  bindInput(input, which) {
    if (!input) return;

    const commitInput = () => {
      const parsed = this.parse(input.value);
      const fallback = which === 'min' ? this.minVal : this.maxVal;
      this.setValue(which, parsed === null ? fallback : parsed, false);
      this.commit();
    };

    input.addEventListener('change', commitInput);
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        commitInput();
        input.blur();
      }
    });
  }

  /** Update one bound, keeping min <= max and within [min, max]. */
  setValue(which, value, commit) {
    value = clamp(value, this.min, this.max);

    if (which === 'min') {
      this.minVal = Math.min(value, this.maxVal);
    } else {
      this.maxVal = Math.max(value, this.minVal);
    }

    this.render();
    if (commit) this.commit();
  }

  commit() {
    this.dispatchEvent(new CustomEvent('filter:change', { bubbles: true }));
  }

  // --- external control (clear-all / cancel dialog) ------------------------

  /** Reset to the full range. Does not dispatch — the caller drives the fetch. */
  reset() {
    this.minVal = this.min;
    this.maxVal = this.max;
    this.render();
  }

  /** Restore handles from the current URL (used when the dialog is dismissed). */
  restoreFromURL() {
    const params = new URLSearchParams(window.location.search);
    const gte = params.get(this.gteName);
    const lte = params.get(this.lteName);
    this.minVal = gte === null ? this.min : clamp(Number(gte) * 100, this.min, this.max);
    this.maxVal = lte === null ? this.max : clamp(Number(lte) * 100, this.min, this.max);
    this.render();
  }
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

if (!customElements.get('price-filter')) {
  customElements.define('price-filter', PriceFilter);
}
